/**
 * MDT (Ambulance Crew) Popups
 * ───────────────────────────
 * Extracted from CrewLoginPage.js during the iter-21 refactor. These are
 * the five modal dialogs that appear over the MDT black screen area. Per
 * operator spec, NONE of them carry an X close button — crews MUST follow
 * the workflow (click / YES / PRESS TO GO) to dismiss each one.
 *
 * The component is purely presentational. All state and mutations live in
 * the parent (CrewLoginPage); popups receive booleans + handlers only.
 */

// ─── Shared window-frame title bar (no X button) ──────────────────────
function TitleBar({ label, darkText }) {
  const bg = darkText
    ? "linear-gradient(180deg, #E8E8F0, #B8B8C8)"
    : "linear-gradient(180deg, #4488CC, #2266AA)";
  const color = darkText ? "#333" : "#FFF";
  return (
    <div
      style={{
        background: bg,
        padding: "5px 10px",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        borderBottom: "1px solid #5577AA",
      }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <svg width="16" height="16" viewBox="0 0 16 16">
          <rect x="1" y="1" width="6" height="6" fill="#00AAFF" rx="1" />
          <rect x="9" y="1" width="6" height="6" fill="#00CC44" rx="1" />
          <rect x="1" y="9" width="6" height="6" fill="#FF4444" rx="1" />
          <rect x="9" y="9" width="6" height="6" fill="#FFAA00" rx="1" />
        </svg>
        <span style={{ fontFamily: "'Segoe UI', Tahoma, sans-serif", fontSize: 13, fontWeight: 600, color }}>
          {label}
        </span>
      </div>
    </div>
  );
}

const overlayStyle = (z) => ({
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: z,
});

const frameStyle = {
  border: "2px solid #6688CC",
  borderRadius: 4,
  overflow: "hidden",
  boxShadow: "0 4px 20px rgba(0,0,0,0.8)",
};

// ─── Individual popups ────────────────────────────────────────────────
export function UnitLoggedOnPopup({ show, onAcknowledge }) {
  if (!show) return null;
  return (
    <div style={overlayStyle(10)}>
      <div
        data-testid="unit-logged-on-popup"
        onClick={onAcknowledge}
        style={{ ...frameStyle, minWidth: 380, cursor: "pointer" }}>
        <TitleBar label="Windows" darkText />
        <div style={{ background: "#000", padding: "36px 40px", display: "flex", alignItems: "center", gap: 16, justifyContent: "center" }}>
          <svg width="40" height="40" viewBox="0 0 40 40">
            <circle cx="20" cy="20" r="18" fill="#00CC44" stroke="#008800" strokeWidth="2" />
            <path d="M12 20 L17 26 L28 14" stroke="#FFF" strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span style={{ fontFamily: "'Courier New', monospace", fontSize: 28, fontWeight: 700, color: "#FFF", letterSpacing: 2 }}>
            UNIT LOGGED ON
          </span>
        </div>
      </div>
    </div>
  );
}

export function CallAssignedPopup({ show, onAcknowledge }) {
  if (!show) return null;
  return (
    <div style={overlayStyle(15)}>
      <div
        data-testid="call-assigned-popup"
        onClick={onAcknowledge}
        style={{ ...frameStyle, minWidth: 420, cursor: "pointer", animation: "flashYellowRed 0.9s infinite" }}>
        <TitleBar label="CALL ASSIGNED" />
        <div style={{ padding: "28px 40px", display: "flex", alignItems: "center", justifyContent: "center", border: "3px inset #CCCC00" }}>
          <span style={{ fontFamily: "'Courier New', monospace", fontSize: 36, fontWeight: 900, color: "#000", letterSpacing: 3 }}>
            CALL ASSIGNED
          </span>
        </div>
      </div>
    </div>
  );
}

export function UpdatePopup({ show, onAcknowledge }) {
  if (!show) return null;
  return (
    <div style={overlayStyle(16)}>
      <div
        data-testid="call-update-popup"
        onClick={onAcknowledge}
        style={{ ...frameStyle, minWidth: 420, cursor: "pointer" }}>
        <TitleBar label="UPDATE" />
        <div style={{ background: "#FFFF00", padding: "28px 40px", display: "flex", alignItems: "center", justifyContent: "center", border: "3px inset #CCCC00" }}>
          <span style={{ fontFamily: "'Courier New', monospace", fontSize: 36, fontWeight: 900, color: "#000", letterSpacing: 3 }}>
            UPDATE
          </span>
        </div>
      </div>
    </div>
  );
}

export function StatusConfirmPopup({ signal, onConfirm, onCancel }) {
  if (!signal) return null;
  return (
    <div style={overlayStyle(10)}>
      <div style={{ ...frameStyle, minWidth: 340, background: "#000" }}>
        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 10, alignItems: "center" }}>
          {/* PRESS TO GO — primary action. Green gradient + white text so
              the active control is unambiguous. Solves the iter-52 bug
              where users accidentally clicked CANCEL because it looked
              identical to the activation button. */}
          <button
            data-testid="confirm-status"
            type="button"
            onClick={(e) => { e.stopPropagation(); onConfirm && onConfirm(); }}
            style={{
              width: "100%", padding: "14px 0",
              background: "linear-gradient(180deg, #00C040, #007020)",
              border: "2px solid #009030", borderRadius: 4,
              fontFamily: "'Courier New', monospace", fontSize: 20, fontWeight: 700,
              color: "#FFF", cursor: "pointer", letterSpacing: 1,
              textShadow: "0 1px 2px rgba(0,0,0,0.4)",
            }}>
            PRESS TO GO {signal}
          </button>
          {onCancel && (
            /* Per operator spec — every 97/98/99 activation prompt offers
               a CANCEL fallback. Visually subordinate (gray) so it never
               competes with PRESS TO GO for the click. */
            <button
              data-testid="cancel-status"
              type="button"
              onClick={(e) => { e.stopPropagation(); onCancel && onCancel(); }}
              style={{
                width: "100%", padding: "12px 0",
                background: "linear-gradient(180deg, #C8C8D0, #A0A0A8)",
                border: "2px solid #888", borderRadius: 4,
                fontFamily: "'Courier New', monospace", fontSize: 16, fontWeight: 700,
                color: "#222", cursor: "pointer", letterSpacing: 1,
              }}>
              CANCEL
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function LogoffConfirmPopup({ show, onYes, onNo }) {
  if (!show) return null;
  return (
    <div style={overlayStyle(20)}>
      <div style={{ ...frameStyle, minWidth: 380 }}>
        <TitleBar label="Confirm Log Off" />
        <div style={{ background: "#000", padding: "24px 30px", textAlign: "center" }}>
          <div style={{ fontFamily: "'Courier New', monospace", fontSize: 20, fontWeight: 700, color: "#FFCC00", marginBottom: 16 }}>
            ARE YOU SURE YOU WANT TO LOG OFF?
          </div>
          <div style={{ display: "flex", justifyContent: "center", gap: 16 }}>
            <button
              data-testid="confirm-logoff-yes"
              onClick={onYes}
              style={{ padding: "8px 30px", background: "#CC3333", color: "#FFF", border: "none", borderRadius: 4, fontSize: 16, fontWeight: 700, cursor: "pointer", fontFamily: "'Courier New', monospace" }}>
              YES
            </button>
            <button
              data-testid="confirm-logoff-no"
              onClick={onNo}
              style={{ padding: "8px 30px", background: "#333", color: "#FFF", border: "1px solid #555", borderRadius: 4, fontSize: 16, fontWeight: 700, cursor: "pointer", fontFamily: "'Courier New', monospace" }}>
              NO
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── New-message popup ────────────────────────────────────────────────
// Renders a CALL-ASSIGNED-style full-screen popup over the MDT black area
// when the crew receives a message via the MESSAGE WAITING button. Format
// matches the operator spec: bold "MESSAGE WAITING" header, a clear
// FROM line ("FROM: <unit_id|dispatch_id>"), HH:MM:SS timestamp, and the
// message body in big DOS-monospace text. No X close — the crew acknowledges
// by clicking OK, which also marks the message read in the parent.
export function MessagePopup({ show, message, onAcknowledge }) {
  if (!show || !message) return null;
  const ts = (() => {
    if (!message.timestamp) return "";
    try {
      const d = new Date(message.timestamp);
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    } catch { return ""; }
  })();
  return (
    <div style={overlayStyle(17)}>
      <div
        data-testid="crew-msg-popup"
        style={{ ...frameStyle, minWidth: 520, maxWidth: 720, cursor: "default" }}>
        <TitleBar label="MESSAGE WAITING" />
        <div style={{
          background: "#FFFF00", padding: "20px 30px", border: "3px inset #CCCC00",
        }}>
          <div style={{
            fontFamily: "'Courier New', monospace", fontSize: 18, fontWeight: 700,
            color: "#000", marginBottom: 4, letterSpacing: 1,
          }}>
            [{ts}] FROM: {message.sender_name || "—"}
          </div>
          <div style={{
            fontFamily: "'Courier New', monospace", fontSize: 26, fontWeight: 900,
            color: "#000", lineHeight: 1.25, whiteSpace: "pre-wrap", marginTop: 12, marginBottom: 18,
          }}>
            {message.body}
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              data-testid="crew-msg-popup-ok"
              onClick={onAcknowledge}
              style={{
                padding: "8px 36px", background: "#008f00", color: "#FFF",
                border: "1px solid #006600", borderRadius: 4, fontSize: 18,
                fontWeight: 800, cursor: "pointer", fontFamily: "'Courier New', monospace",
              }}>
              OK
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
